import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Eraser, Check, Loader2, FileText, PenLine, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Sign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [agree, setAgree] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => { axios.get(`${API}/api/sign/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This signing link is invalid.")); }, [token]);
  useEffect(() => {
    const c = canvasRef.current; if (!c || !data || data.signed) return;
    const ratio = window.devicePixelRatio || 1; const w = c.offsetWidth, h = 200;
    c.width = w * ratio; c.height = h * ratio; c.style.height = `${h}px`;
    const ctx = c.getContext("2d"); ctx.scale(ratio, ratio); ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0f172a";
  }, [data]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 0.1, y + 0.1); ctx.stroke(); setDirty(true); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setDirty(false); };

  const submit = async () => {
    setSaving(true);
    try {
      const c = canvasRef.current; const out = document.createElement("canvas"); out.width = c.width; out.height = c.height;
      const o = out.getContext("2d"); o.fillStyle = "#fff"; o.fillRect(0, 0, out.width, out.height); o.drawImage(c, 0, 0);
      const blob = await new Promise((r) => out.toBlob(r, "image/png"));
      const fd = new FormData(); fd.append("file", new File([blob], "signature.png", { type: "image/png" }));
      const { data: r } = await axios.post(`${API}/api/sign/${token}`, fd);
      setData((d) => ({ ...d, signed: true, signed_at: r.signed_at }));
    } catch (e) { setErr(e?.response?.data?.detail || "Couldn't save your signature. Please try again."); }
    finally { setSaving(false); }
  };

  if (err) return <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center p-6"><div className="trk-card max-w-md text-rose-700" data-testid="sign-error">{err}</div></div>;
  if (!data) return <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#f3f4f6] text-slate-900" data-testid="sign-page">
      <header className="bg-[#0b1f3a] text-white px-5 py-4"><div className="max-w-2xl mx-auto"><div className="text-[11px] uppercase tracking-[0.2em] text-white/60">{data.company}</div><div className="text-xl font-bold">Affidavit signature request</div></div></header>
      <main className="max-w-2xl mx-auto p-5 space-y-4">
        <div className="trk-card space-y-2">
          <div className="text-sm text-slate-600">Hi <b className="text-slate-900">{data.signer_name}</b>, {data.requested_by ? `${data.requested_by} at ` : ""}{data.company} prepared an Affidavit of Service in your name.</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm pt-2">
            <div><span className="text-slate-500">Job</span> <b data-testid="sign-job-number">{data.job_number}</b></div>
            {data.court_case_number && <div><span className="text-slate-500">Case</span> <b>{data.court_case_number}</b></div>}
            <div className="sm:col-span-2"><span className="text-slate-500">Served</span> <b>{data.recipient_name}</b>{data.recipient_address ? <span className="text-slate-600"> · {data.recipient_address}</span> : null}</div>
            {data.court_name && <div className="sm:col-span-2 text-slate-600">{data.court_name}</div>}
          </div>
          <a href={data.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:border-[#0b1f3a]" data-testid="sign-preview-pdf"><FileText className="h-4 w-4" />Read the affidavit (PDF)</a>
        </div>

        {data.signed ? (
          <div className="trk-card flex items-center gap-3 bg-emerald-50 border-emerald-200" data-testid="sign-done">
            <ShieldCheck className="h-8 w-8 text-emerald-700 shrink-0" />
            <div><div className="font-bold text-emerald-900">Signed — thank you!</div><div className="text-sm text-emerald-800">Your signature is now on the affidavit. {data.company} has been notified. You can close this page.</div></div>
          </div>
        ) : (
          <div className="trk-card space-y-3" data-testid="sign-form">
            <label className="flex items-start gap-3 text-sm cursor-pointer"><input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="sign-agree" /><span>I have read the affidavit and declare under penalty of perjury that the facts stated in it are true and correct to the best of my knowledge.</span></label>
            <div className="text-xs text-slate-500 flex items-center gap-1"><PenLine className="h-3.5 w-3.5" />Sign below with your finger or mouse.</div>
            <div className="relative rounded-lg border-2 border-dashed border-slate-300 bg-white overflow-hidden">
              <canvas ref={canvasRef} className="w-full touch-none block" data-testid="sign-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up} />
              <div className="pointer-events-none absolute left-4 right-4 bottom-8 border-b border-slate-300" />
              <div className="pointer-events-none absolute left-4 bottom-2 text-[10px] uppercase tracking-wider text-slate-400">{data.signer_name}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="h-12" onClick={clear} data-testid="sign-clear"><Eraser className="h-4 w-4 mr-1.5" />Clear</Button>
              <Button type="button" className="h-12 bg-[#0f3d2a] hover:bg-[#0b2e20] text-white" onClick={submit} disabled={saving || !dirty || !agree} data-testid="sign-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" />Sign affidavit</>}</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
